import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play, GraduationCap, Globe, Award, Music4, BookOpen, Dumbbell, School, Accessibility } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CourseHighlight } from "@/components/CourseHighlight";
import { PlayAlongBanner } from "@/components/PlayAlongPromo";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/FAQ";
import { AnimatedNotes } from "@/components/AnimatedNotes";
import { PhotoStrip } from "@/components/PhotoStrip";
import { ChampionSpotlight } from "@/components/ChampionSpotlight";
import { CourseCards, WhyYouTube, WhyChoose, LiveQATeaser, CommunityTeaser } from "@/components/MarketingSections";

const STATS = [
  { icon: Music4, value: "45+", label: "Years at the piano" },
  { icon: Globe, value: "25+", label: "Countries performed in" },
  { icon: GraduationCap, value: "30+", label: "Years teaching" },
  { icon: Award, value: "100%", label: "exam pass rate" },
];

export default function Landing() {
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />

      {/* HERO — course first */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-primary/15 blur-[130px]" aria-hidden />
        <AnimatedNotes count={16} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
              <BookOpen size={13} /> The signature course
            </p>
            <h1 data-testid="hero-headline" className="mt-5 font-serif text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Learn to read music in just <span className="bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">10 lessons</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Master sight-reading, rhythm and performance across<br />
              <span className="whitespace-nowrap"><span className="text-foreground">Piano, Guitar, Drums & Voice</span> with</span><br />
              Jason Thompson's proven <span className="whitespace-nowrap">step-by-step framework.</span>
            </p>
            <p className="mt-3 max-w-xl text-base text-foreground/90" data-testid="hero-genres">Gospel, Funk, Jazz, Blues, Soul, R&B, Classical – whatever the genre, The JT Hub can teach you the theory needed for it.</p>
            <p className="mt-3 flex max-w-xl items-start gap-2 text-sm text-muted-foreground" data-testid="hero-accessibility-note">
              <Accessibility size={16} className="mt-0.5 shrink-0 text-primary" />
              <span>Need it easier to see or hear? Tap <b className="text-foreground">Accessibility</b> (bottom-left) for bigger text, high contrast, captions, lyrics and read-aloud.</span>
            </p>
            <p className="mt-6 inline-block rounded-2xl border-2 border-primary/50 bg-primary/15 px-6 py-4 text-2xl font-extrabold leading-snug text-primary shadow-[0_0_30px_-8px_hsl(var(--primary)/0.6)] sm:text-3xl lg:text-4xl" data-testid="hero-strapline">
              Beginner to Intermediate, or Intermediate to Advanced reading in just 10 lessons!
            </p>
            <div>
              <Link to="/pricing#teacher-rates" data-testid="hero-teacher-rates" className="mt-4 inline-flex items-center gap-3 rounded-2xl border-2 border-primary/40 bg-card px-7 py-4 text-xl font-bold text-foreground transition-colors hover:border-primary hover:text-primary sm:text-2xl">
                <School size={22} className="text-primary" /> Teacher rates for whole class sign up <ArrowRight size={18} />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/courses" data-testid="start-course-cta">
                <Button className="rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-8 py-6 font-bold text-slate-950 shadow-[0_0_30px_-4px_hsl(var(--primary)/0.5)] transition-all hover:scale-[1.03]">
                  Start the 10-lesson course <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/trainer" data-testid="hero-training-cta">
                <Button variant="outline" className="rounded-full border-primary/30 px-8 py-6 font-semibold text-primary hover:bg-primary/10">
                  Try the sight-reading trainer
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              {STATS.map((s) => (
                <div key={s.label} className="flex items-center gap-2" data-testid={`hero-stat-${s.label}`}>
                  <s.icon size={18} className="text-primary" />
                  <span className="font-serif text-xl font-bold">{s.value}</span>
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
            <div className="overflow-hidden rounded-[28px] border border-border shadow-2xl shadow-black/40">
              <img src="/media/jason-hero.jpg" alt="Jason Thompson at the piano" data-testid="hero-portrait" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 rounded-2xl bg-card px-5 py-3 shadow-lg ring-1 ring-border">
              <p className="font-serif text-lg font-bold">Jason Thompson</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary">Your teacher · The JT Hub</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-8 py-14 text-center" data-testid="home-welcome">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">A welcome from Jason</p>
        <p className="mt-5 font-serif text-xl leading-relaxed sm:text-2xl" data-testid="home-welcome-message">
          “Hi! My name is Jason Thompson. Music has taken me around the world and I have met and also played for some amazing people along the way. Music inspires and connects. I can help it do that for you. Welcome to The JT Hub.”
        </p>
        <p className="mt-4 text-sm text-muted-foreground">— Jason Thompson, pianist, Musical Director & educator</p>
        <div className="mt-8 text-left" data-testid="home-showreel"><CourseHighlight videoKey="showreel" path="/" autoPlay title="The JT Hub — learn to read music" heading="Watch the JT Hub showreel" /></div>
      </section>

      <PhotoStrip />

      <ChampionSpotlight />

      {/* COURSES */}
      <CourseCards />

      {/* WHY NOT YOUTUBE */}
      <WhyYouTube />

      {/* WHY CHOOSE */}
      <WhyChoose />

      {/* LIVE Q&A */}
      <LiveQATeaser />

      {/* TRAINING teaser */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-20">
        <div className="grid items-center gap-8 rounded-3xl border border-border bg-card p-8 sm:p-10 lg:grid-cols-2">
          <div>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><Dumbbell size={14} /> Training</p>
            <h2 className="mt-3 font-serif text-3xl font-extrabold tracking-tight sm:text-4xl">Practice between lessons</h2>
            <p className="mt-3 text-muted-foreground">Sharpen your reading with the Sight-Reading Trainer, Quizzes and a daily challenge.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/trainer"><Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Open the trainer <ArrowRight size={15} className="ml-2" /></Button></Link>
              <Link to="/quizzes"><Button variant="outline" className="rounded-full border-border hover:border-primary hover:text-primary">Take a quiz</Button></Link>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-2xl bg-background p-8">
            <AnimatedNotesInline />
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <CommunityTeaser />

      {/* FAQ */}
      <div className="mx-auto max-w-7xl px-4 sm:px-8"><PlayAlongBanner testId="home-playalong-banner" /></div>

      <section className="mx-auto max-w-4xl px-4 sm:px-8 py-8 lg:py-12">
        <h2 className="font-serif text-3xl font-extrabold tracking-tight">Questions, answered</h2>
        <div className="mt-6"><FAQ /></div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500 to-amber-600 p-8 text-slate-950 sm:p-12">
          <div className="relative flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <p className="font-serif text-2xl font-extrabold sm:text-3xl">Ready to read music with confidence?</p>
              <p className="mt-2 font-medium text-slate-900/80">Ten lessons. One clear path. Taught by me.</p>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-center">
              <Link to="/courses" data-testid="final-cta">
                <Button className="rounded-full bg-slate-950 px-7 py-6 font-bold text-amber-400 hover:bg-slate-900">Explore the courses <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
              <p className="text-sm font-semibold text-slate-900/80" data-testid="final-cta-menu-hint">or click on the Menu tab for more information</p>
            </div>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8" data-testid="landing-local-links">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Lessons in Dartford, Kent and online</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[["/piano-lessons-dartford", "Piano lessons in Dartford"], ["/singing-lessons-dartford", "Singing lessons in Dartford"], ["/music-theory-lessons-dartford", "Music theory lessons in Dartford"], ["/articles", "Free music theory articles"]].map(([to, label]) => (
            <Link key={to} to={to} data-testid={`landing-local-${to.slice(1)}`} className="rounded-full border border-border px-4 py-2 text-sm font-semibold transition-colors hover:border-primary hover:text-primary">{label}</Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function AnimatedNotesInline() {
  return (
    <div className="relative flex h-40 w-full items-center justify-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_0_40px_-6px_hsl(var(--primary)/0.6)]"><Play size={26} className="ml-1" /></span>
      <AnimatedNotes count={10} />
    </div>
  );
}
